import { z } from "zod";
import { err, ok, type Result } from "./result";
import { formatZodIssues } from "./zod";

/**
 * Minimal, recursive model of a TipTap / ProseMirror document. We don't try to
 * enumerate every node type — just enough structure to validate input at the
 * boundary and to extract plain text for search, diffing and email rendering.
 */
export type RichTextMark = {
  readonly type: string;
  readonly attrs?: Record<string, unknown>;
};

export type RichTextNode = {
  readonly type: string;
  readonly attrs?: Record<string, unknown>;
  readonly marks?: readonly RichTextMark[];
  readonly text?: string;
  readonly content?: readonly RichTextNode[];
};

export type RichTextDoc = {
  readonly type: "doc";
  readonly content?: readonly RichTextNode[];
};

const markSchema: z.ZodType<RichTextMark> = z.object({
  type: z.string(),
  attrs: z.record(z.unknown()).optional(),
});

const nodeSchema: z.ZodType<RichTextNode> = z.lazy(() =>
  z.object({
    type: z.string(),
    attrs: z.record(z.unknown()).optional(),
    marks: z.array(markSchema).optional(),
    text: z.string().optional(),
    content: z.array(nodeSchema).optional(),
  }).superRefine((node, ctx) => {
    if (node.type === "text" && node.text === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["text"],
        message: "text nodes must include text",
      });
    }

    if (node.type === "text" && node.content !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["content"],
        message: "text nodes must not include child content",
      });
    }

    if (node.type !== "text" && node.text !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["text"],
        message: "only text nodes may include text",
      });
    }
  }),
);

export const richTextDocSchema: z.ZodType<RichTextDoc> = z.object({
  type: z.literal("doc"),
  content: z.array(nodeSchema).optional(),
});

export const emptyDoc = (): RichTextDoc => ({ type: "doc", content: [] });

export type RichTextParseError = {
  readonly kind: "invalidRichTextDoc";
  readonly message: string;
};

export const richTextParseErrorMessage = (error: RichTextParseError): string =>
  "Rich text document did not match the expected schema: " + error.message;

export const parseRichTextDocResult = (
  value: unknown,
): Result<RichTextDoc, RichTextParseError> => {
  const result = richTextDocSchema.safeParse(value);
  return result.success
    ? ok(result.data)
    : err({ kind: "invalidRichTextDoc", message: formatZodIssues(result.error.issues) });
};

export const richTextDocOrEmpty = (value: unknown): RichTextDoc => {
  const result = parseRichTextDocResult(value);
  return result.ok ? result.value : emptyDoc();
};


/** A doc holding a single paragraph of plain text. */
export const textDoc = (text: string): RichTextDoc => ({
  type: "doc",
  content: [{ type: "paragraph", content: [{ type: "text", text }] }],
});

const BLOCK_TYPES = new Set([
  "paragraph",
  "heading",
  "listItem",
  "blockquote",
  "tableRow",
]);

/**
 * Flatten a rich-text doc to plain text. Block-level nodes are separated by
 * newlines so downstream diffing/search keeps a sensible word stream.
 */
export const docToPlainText = (node: RichTextNode | RichTextDoc): string => {
  if ("type" in node && node.type === "hardBreak") return "\n";
  const self = "text" in node && node.text ? node.text : "";
  const children = (node.content ?? []).map(docToPlainText).join("");
  const out = self + children;
  return "type" in node && BLOCK_TYPES.has(node.type) ? out + "\n" : out;
};

export const isEmptyDoc = (doc: RichTextDoc): boolean =>
  docToPlainText(doc).trim().length === 0;
